import { Info } from 'lucide-react';
import { Link } from 'react-router-dom';

import { CardTitle } from 'components/ui/card';
import { Sheet, SheetContent, SheetDescription, SheetHeader } from 'components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip';
import { formatPrice } from 'utils/formatters';

import { CommissionItem, CommissionStatus } from '../hooks/useCommissions';

const STATUS_COLORS: Record<CommissionStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-900',
  confirmed: 'bg-blue-100 text-blue-900',
  updated: 'bg-blue-100 text-blue-900',
  finalized: 'bg-green-100 text-green-900',
  refunded: 'bg-gray-200 text-gray-900',
  expired: 'bg-gray-200 text-gray-700',
};

const formatMoney = (m: { amountSubunits: number; currencyCode: string }) =>
  formatPrice(m.amountSubunits / 100, m.currencyCode);

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

type Props = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  commission?: CommissionItem;
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between border-b py-3 last:border-b-0">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className="text-sm">{children}</span>
  </div>
);

export const CommissionDetailSheet = ({ open, onOpenChange, commission }: Props) => {
  const ryeOwes = commission?.settlementDirection === 'rye_owes_developer';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-6/12 overflow-y-auto">
        <SheetHeader className="space-y-2">
          <CardTitle className="flex items-center gap-2">
            {commission && (
              <span
                className={`inline-block rounded-md px-2 py-0.5 text-xs capitalize ${
                  STATUS_COLORS[commission.status] ?? 'bg-gray-100'
                }`}
              >
                {commission.status}
              </span>
            )}
            <span className="font-mono text-sm">{commission?.id}</span>
          </CardTitle>
          {commission && (
            <SheetDescription>
              <Link to={`/checkout-intents/${commission.checkoutIntentId}`} className="underline">
                View checkout intent →
              </Link>
            </SheetDescription>
          )}
        </SheetHeader>

        {commission && (
          <div className="mt-10 space-y-10 pb-10">
            <section>
              <h3 className="mb-3 text-sm font-medium">Amounts</h3>
              <div className="rounded-lg border px-3">
                <Row label="Your commission">
                  <span className="text-green-600">
                    {formatMoney(commission.developerCommission)}
                  </span>
                </Row>
                <Row label="Rye fee">{formatMoney(commission.ryeFee)}</Row>
              </div>
            </section>

            <section>
              <h3 className="mb-3 flex items-center gap-1.5 text-sm font-medium">
                Settlement
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 cursor-help text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Receivables are paid out via bank transfer. Payables are invoiced monthly.
                  </TooltipContent>
                </Tooltip>
              </h3>
              <div className="rounded-lg border px-3">
                {ryeOwes ? (
                  <Row label="Receivable from Rye">
                    <span className="text-green-600">
                      ↑ {formatMoney(commission.developerCommission)}
                    </span>
                  </Row>
                ) : (
                  <Row label="Payable to Rye">
                    <span className="text-red-600">↓ {formatMoney(commission.ryeFee)}</span>
                  </Row>
                )}
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-sm font-medium">Timeline</h3>
              <div className="rounded-lg border px-3">
                <Row label="Created">{formatDateTime(commission.createdAt)}</Row>
                <Row label="Updated">{formatDateTime(commission.updatedAt)}</Row>
                {commission.finalizedAt && (
                  <Row label="Finalized">{formatDateTime(commission.finalizedAt)}</Row>
                )}
              </div>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
